import {
  BlogPostGrid,
  HeroImageCol,
  HeroImageRow,
  HeroResponsiveGrid,
  PostContentCol,
  PostContentRow,
  Title,
} from './styles';
import { Col, Row } from 'react-styled-flexboxgrid';

import AuthorProfile from '@/components/Blog/AuthorProfile';
import BlogPosts from '@/components/BlogPosts';
import Cta from '@/components/ui/buttons/Cta';
import LoadingIndicator from '@/components/LoadingIndicator';
import NotFoundPage from '@/components/404';
import PostContent from '@/components/Blog/PostContent';
import PostSocials from '@/components/Blog/PostSocials';
import PreviewCompatibleImage from '@/components/PreviewCompatibleImage';
import React from 'react';
import ResponsiveGrid from '@/components/ui/ResponsiveGrid';
import Section from '@/components/ui/Section';
import SectionHeadline from '@/components/ui/elements/SectionHeadline';
import TagList from '@/components/Blog/TagList';
import { groupTags } from '@/utils/helpers';
import { useRouter } from 'next/router';
import retiredTags from '../../../../retired-tags';

interface LayoutProps {
  post: Record<string, unknown>;
  permalink?: string;
  blogPostCopy?: Record<string, unknown>;
  postList: [];
}

const NormalLayout: React.FC<LayoutProps> = ({ post, permalink, blogPostCopy, postList }) => {
  const router = useRouter();

  if (router.isFallback) {
    return (
      <Section bgColor="white">
        <LoadingIndicator text="Loading" />
      </Section>
    );
  }

  if (!post) {
    return <NotFoundPage />;
  }

  const { title, author, tags, publishedAt, metaDescription, normalLayout, mainImage } = post as {
    title: string;
    author: unknown;
    tags: unknown[];
    publishedAt: string;
    metaDescription: string;
    normalLayout: { body?: unknown };
    mainImage: { image: { alt: string; [key: string]: unknown } };
  };
  const shortDesc = metaDescription || '';
  const hasContent = !!normalLayout?.body;
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const date = publishedAt ? new Intl.DateTimeFormat('en-US', options).format(new Date(publishedAt)) : null;
  return (
    <Section>
      <ResponsiveGrid>
        <header>
          <Row>
            <Col xs={12} sm={10} smOffset={1}>
              <Title xs={24} sm={40} md={40} lg={40}>
                {title}
              </Title>
            </Col>
          </Row>
        </header>
      </ResponsiveGrid>

      <HeroResponsiveGrid>
        <HeroImageRow>
          <HeroImageCol xs={12} sm={12} md={10} mdOffset={1}>
            {mainImage && (
              <PreviewCompatibleImage
                layout="responsive"
                priority
                sizes="(min-width: 1280px) 1000px, (min-width: 768px) 83vw, 100vw"
                imageInfo={{
                  image: mainImage.image,
                  alt: mainImage.image.alt || title,
                  width: 1200,
                }}
              />
            )}
          </HeroImageCol>
        </HeroImageRow>
      </HeroResponsiveGrid>

      <ResponsiveGrid>
        <PostContentRow>
          <PostContentCol xs={12} sm={12} md={7} mdOffset={1}>
            {hasContent && (
              <Row>
                <Col xs={12}>
                  <PostContent content={normalLayout.body} />
                </Col>
              </Row>
            )}

            {tags && tags.length > 0 && (
              <Row>
                <Col xs={12}>
                  <TagList tags={groupTags(tags).filter((tag) => !retiredTags.includes(tag.slug))} />
                </Col>
              </Row>
            )}

            {date && (
              <Row style={{ marginTop: '24px' }}>
                <Col xs={12}>{date}</Col>
              </Row>
            )}

            {author && (
              <Row style={{ marginTop: '80px', marginBottom: '0rem' }}>
                <Col xs={12}>
                  <AuthorProfile author={author} />
                </Col>
              </Row>
            )}
            {blogPostCopy && (
              <Row center="sm" style={{ textAlign: 'center' }}>
                <Col xs={12}>
                  <SectionHeadline as="h3" xs={32} sm={32} md={32} lg={32}>
                    We love working with other bright minds.
                  </SectionHeadline>
                  <Cta to={(blogPostCopy as { bottomCtaUrl: string }).bottomCtaUrl}>
                    {(blogPostCopy as { bottomCtaText: string }).bottomCtaText}
                  </Cta>
                </Col>
              </Row>
            )}
          </PostContentCol>

          <Col xs={12} sm={12} md={3}>
            <PostSocials permalink={permalink} title={title} description={shortDesc} image={mainImage} inBlogPost />
          </Col>
        </PostContentRow>
      </ResponsiveGrid>

      <BlogPostGrid>
        <Row middle="xs" center="xs">
          <Col xs={12}>
            <header>
              <SectionHeadline>{(blogPostCopy as { postListingHeadline: string }).postListingHeadline}</SectionHeadline>
            </header>
            <BlogPosts posts={postList} withReadmore={false} />
          </Col>
        </Row>
      </BlogPostGrid>
    </Section>
  );
};

export default NormalLayout;
